import { __awaiter } from 'tslib';
import { Env } from '@navify-platform/env';
import { renderUrlQueryParams, setWindowUrl, getWindowUrl, getUrlQueryParam, urlGetDomain } from '@navify-platform/url';
import { ExtError } from '@navify-platform/error';
import { Event } from '@navify-platform/event';
import { IframeLink } from '@navify-platform/iframe-link';
import { evalNewRandomKey } from '@navify-platform/random';
import { Base64 } from 'js-base64';
import { getRequest } from '@navify-platform/http';
import { iframeRequest, iframePopup } from '@navify-platform/iframe-request';

/**
 * @hidden
 * Authentication query param enum.
 */
var AuthQueryParam;
(function (AuthQueryParam) {
    /** Authentication request query param name. */
    AuthQueryParam["request"] = "authreq";
    /** Authentication response query param name. */
    AuthQueryParam["response"] = "authres";
    /** Authentication redirect query param name. */
    AuthQueryParam["redirection"] = "authred";
    /** Authentication success query param name. */
    AuthQueryParam["success"] = "success";
    /** Authentication state query param name. */
    AuthQueryParam["state"] = "state";
    /** Authentication context param name. */
    AuthQueryParam["context"] = "authctx";
})(AuthQueryParam || (AuthQueryParam = {}));
/**
 * @hidden
 * Authentication event type enum.
 */
var AuthEventType;
(function (AuthEventType) {
    /** Session end event type. */
    AuthEventType["sessionStart"] = "SESSION_START";
    /** Before session expiration event type. */
    AuthEventType["beforeSessionExpiration"] = "BEFORE_SESSION_EXPIRATION";
    /** Session expiration event type. */
    AuthEventType["sessionExpiration"] = "SESSION_EXPIRATION";
    /** Session end event type. */
    AuthEventType["sessionEnd"] = "SESSION_END";
    /** Before login event type. */
    AuthEventType["beforeLogin"] = "BEFORE_LOGIN";
    /** After login event type. */
    AuthEventType["afterLogin"] = "AFTER_LOGIN";
    /** Before parent login event type. */
    AuthEventType["beforeParentLogin"] = "BEFORE_PARENT_LOGIN";
    /** After parent login event type. */
    AuthEventType["afterParentLogin"] = "AFTER_PARENT_LOGIN";
    /** Before window login event type (uses event result). */
    AuthEventType["beforeWindowLogin"] = "BEFORE_WINDOW_LOGIN";
    /** After window login event type. */
    AuthEventType["afterWindowLogin"] = "AFTER_WINDOW_LOGIN";
    /** Before logout event type. */
    AuthEventType["beforeLogout"] = "BEFORE_LOGOUT";
    /** After logout event type. */
    AuthEventType["afterLogout"] = "AFTER_LOGOUT";
    /** Before parent logout event type. */
    AuthEventType["beforeParentLogout"] = "BEFORE_PARENT_LOGOUT";
    /** After parent logout event type. */
    AuthEventType["afterParentLogout"] = "AFTER_PARENT_LOGOUT";
    /** Before window logout event type (uses event result). */
    AuthEventType["beforeWindowLogout"] = "BEFORE_WINDOW_LOGOUT";
    /** After window logout event type. */
    AuthEventType["afterWindowLogout"] = "AFTER_WINDOW_LOGOUT";
})(AuthEventType || (AuthEventType = {}));
/**
 * @hidden
 * Authentication request return by enum.
 */
var AuthRequestReturnBy;
(function (AuthRequestReturnBy) {
    AuthRequestReturnBy["redirect"] = "REDIRECT";
    AuthRequestReturnBy["iframe"] = "IFRAME";
})(AuthRequestReturnBy || (AuthRequestReturnBy = {}));
/**
 * @hidden
 * Authentication redirection type enum.
 */
var AuthRedirectionType;
(function (AuthRedirectionType) {
    AuthRedirectionType["logout"] = "LOGOUT";
})(AuthRedirectionType || (AuthRedirectionType = {}));
/**
 * Login reason.
 */
var AuthLoginReason;
(function (AuthLoginReason) {
    AuthLoginReason["loginFailed"] = "loginFailed";
    AuthLoginReason["sessionExpired"] = "sessionExpired";
    AuthLoginReason["codeExpired"] = "codeExpired";
    AuthLoginReason["notAuthorized"] = "notAuthorized";
    AuthLoginReason["errorOccurred"] = "errorOccurred";
})(AuthLoginReason || (AuthLoginReason = {}));
/**
 * Logout reason.
 */
var AuthLogoutReason;
(function (AuthLogoutReason) {
    AuthLogoutReason["userAction"] = "userAction";
    AuthLogoutReason["sessionExpired"] = "sessionExpired";
    AuthLogoutReason["errorOccurred"] = "errorOccurred";
})(AuthLogoutReason || (AuthLogoutReason = {}));

/**
 * Authentication options default values.
 */
const AUTH_OPTIONS_DEFAULTS = {
    sessionExpirationBeforeTime: 60,
    sessionPollingIntervalTime: 10 * 60,
    sessionAutoRefresh: false,
    redirectUrlUseHash: false,
    identificationFormSkip: false,
    customPageTitle: null,
    customBackgroundStyle: null,
    customHeader: null,
    customHeadline: null,
    customCaption: null,
    customFooter: null,
    i18n: false,
};
/**
 * Auth logout reason - log message map.
 */
const AUTH_LOGOUT_REASON_LOG_MESSAGES = {
    [AuthLogoutReason.userAction]: 'User action',
    [AuthLogoutReason.sessionExpired]: 'Session expired',
    [AuthLogoutReason.errorOccurred]: 'Error occurred',
};
/**
 * Auth link parent request options default values.
 */
const AUTH_LINK_PARENT_REQUEST_OPTIONS_DEFAULTS = {
    timeout: 5000, // 5 seconds
};
/**
 * Auth link windows request options default values.
 */
const AUTH_LINK_WINDOWS_REQUEST_OPTIONS_DEFAULTS = {
    timeout: 5000, // 5 seconds
};

/**
 * @hidden
 */
function encodeQuery(query) {
    try {
        const queryEncoded = Base64.encode(JSON.stringify(query));
        return queryEncoded;
    }
    catch (err) {
        return null;
    }
}
/**
 * @hidden
 */
function decodeQuery(queryEncoded) {
    try {
        const query = JSON.parse(Base64.decode(queryEncoded));
        return query;
    }
    catch (err) {
        return null;
    }
}

const SESSION_KEY_LENGTH = 16;
/**
 * @hidden
 */
function sessionSet(value, id) {
    const setId = id || evalNewRandomKey(SESSION_KEY_LENGTH);
    const valueEncoded = encodeQuery(value);
    window.sessionStorage[setId] = valueEncoded;
    return setId;
}
/**
 * @hidden
 */
function sessionGet(id) {
    const valueEncoded = window.sessionStorage[id];
    const value = decodeQuery(valueEncoded);
    return value;
}
/**
 * @hidden
 */
function evalAuthSessionExpiration(authSession, authOptions) {
    const expiresAt = authSession.expiresAt;
    const expiresIn = expiresAt - Math.round(Date.now() / 1000);
    const beforeExpiresIn = expiresIn - authOptions.sessionExpirationBeforeTime;
    return { expiresAt, expiresIn, beforeExpiresIn };
}
/**
 * @hidden
 */
function validateAuthSession(authSession, authOptions) {
    if (!authSession) {
        return null;
    }
    const { expiresIn } = evalAuthSessionExpiration(authSession, authOptions);
    if (expiresIn <= 0) {
        return null;
    }
    return authSession;
}
/**
 * @hidden
 */
function immutablyEvalAuthSession(authSessionNew, authSessionBase) {
    if (authSessionNew === authSessionBase) {
        return authSessionBase;
    }
    if ((!authSessionNew) ||
        (!authSessionBase) ||
        (authSessionNew.expiresAt !== authSessionBase.expiresAt) ||
        (authSessionNew.profile.username !== authSessionBase.profile.username)) {
        return authSessionNew;
    }
    return authSessionBase;
}

const ɵ0 = (error) => __awaiter(void 0, void 0, void 0, function* () { var _a; return (_a = error === null || error === void 0 ? void 0 : error.faults) === null || _a === void 0 ? void 0 : _a.httpServerError; });
const GET_REQUEST_OPTIONS = {
    retryIf: ɵ0,
    retryAfter: 1000,
    retryTimes: 2,
};
/**
 * @hidden
 */
class AuthHttpClient {
    constructor(authEnv) {
        this.authEnv = authEnv;
    }
    getSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const url = `${this.authEnv.platformApiUrl}/api/v1/auth/session-info`;
                const response = yield getRequest(url, Object.assign({}, GET_REQUEST_OPTIONS));
                const session = {
                    expiresAt: response.expires,
                    profile: {
                        username: response.profile.preferred_username,
                        displayName: response.profile.name,
                    },
                };
                return session;
            }
            catch (error) {
                if (error.faults && !!error.faults.httpUnauthorized) {
                    return null;
                }
                throw new ExtError({ getSessionError: true }, error);
            }
        });
    }
    refreshSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const url = `${this.authEnv.platformApiUrl}/api/v1/auth/refresh`;
                yield getRequest(url, Object.assign({}, GET_REQUEST_OPTIONS));
                const session = yield this.getSession();
                return session;
            }
            catch (error) {
                throw new ExtError({
                    refreshSessionError: true,
                    noSession: error.faults && !!error.faults.httpUnauthorized,
                }, error);
            }
        });
    }
    appGetAuthStorage(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield iframeRequest(`${this.authEnv.authUiUrl}/storage/get.html`, { key });
            return response.value;
        });
    }
    appLoginRedirect(loginRequest, useHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const authreq = encodeQuery(loginRequest);
            const appLoginUrl = renderUrlQueryParams(`${this.authEnv.authUiUrl}/login`, { [AuthQueryParam.request]: authreq });
            setWindowUrl(appLoginUrl);
        });
    }
    appLoginIframePopup(loginRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            const appLoginUrl = `${this.authEnv.authUiUrl}/login`;
            const loginResponse = yield iframePopup(appLoginUrl, { loginRequest });
            return loginResponse;
        });
    }
    appLogoutRedirect(logoutRequest, useHash) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { returnBy, returnTo, reason, logMessage } = logoutRequest;
                const logoutStyle = {
                    [AuthRequestReturnBy.redirect]: 'REDIRECT',
                    [AuthRequestReturnBy.iframe]: 'IFRAME',
                }[returnBy];
                const authred = encodeQuery({ type: AuthRedirectionType.logout, logoutRequest });
                const returnToQuery = renderUrlQueryParams(returnTo, { [AuthQueryParam.redirection]: authred }, useHash);
                const messagePcs = [];
                if (reason) {
                    messagePcs.push(`Reason: ${AUTH_LOGOUT_REASON_LOG_MESSAGES[reason]}`);
                }
                if (logMessage) {
                    messagePcs.push(`Log message: ${logMessage}`);
                }
                const message = messagePcs.join('; ') || undefined;
                const logoutUrl = renderUrlQueryParams(`${this.authEnv.platformApiUrl}/api/v1/auth/logout`, { logout_style: logoutStyle, post_logout_redirect_uri: returnToQuery, message });
                setWindowUrl(logoutUrl);
            }
            catch (error) {
                throw new ExtError({
                    logoutError: true,
                    noSession: error.faults && !!error.faults.httpUnauthorized,
                }, error);
            }
        });
    }
}

function isDefined(...values) {
    return values.reduce((acc, value) => (acc && value !== undefined), true);
}
function makeDefined(value) {
    return isDefined(value) ? value : null;
}
function isSet(...values) {
    return values.reduce((acc, value) => (acc && value !== undefined && value !== null), true);
}
const VALUE_STR2BOOL_TRUE_VALUES = ['yes', 'true', 't', 'y', '1'];
const VALUE_STR2BOOL_FALSE_VALUES = ['no', 'false', 'f', 'n', '0'];
function str2bool(value) {
    if (value === null) {
        return null;
    }
    if (typeof value === 'string') {
        const loweredValue = value.toLowerCase();
        if (VALUE_STR2BOOL_TRUE_VALUES.indexOf(loweredValue) !== -1) {
            return true;
        }
        if (VALUE_STR2BOOL_FALSE_VALUES.indexOf(loweredValue) !== -1) {
            return false;
        }
    }
    return !!value;
}

/**
 * @hidden
 */
function replaceWindowHistoryState(title, url) {
    window.history.replaceState(history.state, title, url);
}

function createTimer(handler, time, interval = false) {
    const timeout = interval ?
        window.setInterval(handler, time) :
        window.setTimeout(handler, time);
    const destroy = () => {
        interval ?
            window.clearInterval(timeout) :
            window.clearTimeout(timeout);
    };
    return { destroy };
}

const AUTH_LINK_PARENT_REQUEST_TYPE_GET_SESSION = 'parent:getSession';
const AUTH_LINK_PARENT_REQUEST_TYPE_LOGIN = 'parent:login';
const AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGIN_RETURN = 'parent:getLoginReturn';
const AUTH_LINK_PARENT_REQUEST_TYPE_REFRESH_SESSION = 'parent:refreshSession';
const AUTH_LINK_PARENT_REQUEST_TYPE_LOGOUT = 'parent:logout';
const AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGOUT_RETURN = 'parent:getLogoutReturn';
const AUTH_LINK_WINDOW_REQUEST_EVENT_SESSION_CHANGE = 'window:sessionChange';
const AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGIN = 'window:eventBeforeLogin';
const AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGIN = 'window:eventAfterLogin';
const AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGOUT = 'window:eventBeforeLogout';
const AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGOUT = 'window:eventAfterLogout';
/**
 * Authentication class.
 */
class Auth {
    constructor(authConfInput = {}) {
        this.authConfInput = authConfInput;
        this.authHttpClient = null;
        this.authSession = null;
        this.loginResponse = null;
        this.logoutResponse = null;
        this.eventHandlers = [];
        this.beforeExpirationTimer = null;
        this.expirationTimer = null;
        this.pollingTimer = null;
        this.authLink = null;
    }
    /**
     * Creates and initializes an instance.
     * @returns Promise resolved when the initialization is done.
     */
    static init(authConfInput) {
        return __awaiter(this, void 0, void 0, function* () {
            const auth = new Auth(authConfInput);
            yield auth.init();
            return auth;
        });
    }
    /**
     * Initializes the instance.
     * @returns Promise resolved when the initialization is done.
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.initConf();
            yield this.initHttpClient();
            yield this.initLink();
        });
    }
    /**
     * Destroys the instance.
     * @returns Promise resolved when the destruction is done.
     */
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.authLink.destroy();
        });
    }
    initConf() {
        return __awaiter(this, void 0, void 0, function* () {
            this.authOptionsInput = Object.assign(Object.assign({}, (this.authConfInput || {})), { env: undefined });
            this.authOptions = Object.assign(Object.assign({}, AUTH_OPTIONS_DEFAULTS), this.authOptionsInput);
            const env = this.authConfInput.env || new Env();
            const platformApiUrl = yield env.getPlatformApiUrl();
            const authUiUrl = yield env.getAuthUiUrl();
            const appAlias = yield env.getAppAlias();
            const tenantAlias = yield env.getTenantAlias();
            if (!isSet(platformApiUrl, authUiUrl, appAlias, tenantAlias)) {
                throw new ExtError({ envInvalid: true });
            }
            this.authEnv = { platformApiUrl, authUiUrl, appAlias, tenantAlias };
        });
    }
    initHttpClient() {
        return __awaiter(this, void 0, void 0, function* () {
            this.authHttpClient = new AuthHttpClient(this.authEnv);
        });
    }
    initLink() {
        return __awaiter(this, void 0, void 0, function* () {
            this.authLink = new IframeLink((request, origin, source) => __awaiter(this, void 0, void 0, function* () {
                return yield this.linkParentHandle(request.type, request.data, origin, source);
            }), (request, origin, source) => __awaiter(this, void 0, void 0, function* () {
                return yield this.linkWindowHandle(request.type, request.data, origin, source);
            }));
            yield this.authLink.init();
        });
    }
    /**
     * Requests and returns the session data.
     * @returns Promised resolved with the session object.
     */
    getSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const session = this.authLink.hasParent() ?
                    yield this.getSessionParentRequest() :
                    yield this.getSessionHandle();
                yield this.applySession(session);
                return this.authSession;
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    getSessionHandle() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.authHttpClient.getSession();
        });
    }
    getSessionParentRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_GET_SESSION);
            return response;
        });
    }
    getSessionParentHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getSession();
        });
    }
    getSessionSilent() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return this.getSession();
            }
            catch (error) {
                return null;
            }
        });
    }
    login(args) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const loginRequest = {
                    env: this.authEnv,
                    options: this.authOptionsInput,
                    returnBy: makeDefined(args === null || args === void 0 ? void 0 : args.returnBy),
                    returnTo: makeDefined(args === null || args === void 0 ? void 0 : args.returnTo) || getWindowUrl(),
                    username: makeDefined(args === null || args === void 0 ? void 0 : args.username),
                    stateMap: { ['']: makeDefined(args === null || args === void 0 ? void 0 : args.state) },
                    reason: makeDefined(args === null || args === void 0 ? void 0 : args.reason),
                };
                yield this.loginRequest(loginRequest);
                return this.getLoginReturn();
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    /**
     * Initiates the redirect type logout float.
     * @param AuthLogoutRedirectArgs Logout redirect arguments object.
     * @returns Promise resolved with the login flow is initiated.
     */
    loginRedirect(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.login({
                returnBy: AuthRequestReturnBy.redirect,
                returnTo: (args === null || args === void 0 ? void 0 : args.returnTo) || getWindowUrl(),
                username: args === null || args === void 0 ? void 0 : args.username,
                state: args === null || args === void 0 ? void 0 : args.state,
                reason: args === null || args === void 0 ? void 0 : args.reason,
            });
        });
    }
    /**
     * Loads the login UI application into an iframe.
     * @param AuthLoginIframeArgs Login iframe arguments object.
     * @returns Promise resolved with the login return object.
     */
    loginIframe(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.login({
                returnBy: AuthRequestReturnBy.iframe,
                username: args === null || args === void 0 ? void 0 : args.username,
                state: args === null || args === void 0 ? void 0 : args.state,
                reason: args === null || args === void 0 ? void 0 : args.reason,
            });
        });
    }
    loginRequest(loginRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.authLink.hasParent()) {
                return this.loginParentRequest(loginRequest);
            }
            return this.loginHandle(loginRequest);
        });
    }
    loginHandle(loginRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.beforeLogin);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGIN);
            if (loginRequest.returnBy === AuthRequestReturnBy.redirect) {
                yield this.applyLoginResponse(null);
                yield this.authHttpClient.appLoginRedirect(loginRequest, this.authOptions.redirectUrlUseHash);
            }
            else if (loginRequest.returnBy === AuthRequestReturnBy.iframe) {
                const loginResponse = yield this.authHttpClient.appLoginIframePopup(loginRequest);
                yield this.applyLoginResponse(loginResponse);
            }
            else {
                throw new ExtError({ returnByInvalid: true });
            }
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGIN);
            yield this.trigger(AuthEventType.afterLogin);
            return this.loginResponse;
        });
    }
    loginParentRequest(loginRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_LOGIN, loginRequest, { timeout: null });
            return response;
        });
    }
    loginParentHandle(loginRequest, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const beforeLoginEvent = yield this.trigger(AuthEventType.beforeWindowLogin, null, {
                returnBy: loginRequest.returnBy,
                returnTo: loginRequest.returnTo ? getWindowUrl() : null,
                username: loginRequest.username,
                state: null,
                reason: loginRequest.reason,
            });
            const { returnBy, returnTo, username, state, reason } = beforeLoginEvent.result;
            const response = yield this.loginRequest({
                env: this.authEnv,
                options: this.authOptionsInput,
                returnBy,
                returnTo,
                username,
                stateMap: {
                    ['']: state,
                    [origin]: loginRequest.stateMap[''],
                },
                reason,
            });
            yield this.trigger(AuthEventType.afterWindowLogin);
            return response;
        });
    }
    /**
     * Requests and returns login response data.
     * @returns Promise resolved with the login response data.
     */
    getLoginReturn() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const loginResponse = yield this.getLoginReturnRequest();
                if (!loginResponse) {
                    return null;
                }
                const responseStateMap = loginResponse.stateMap || {};
                return {
                    success: loginResponse.success || null,
                    state: responseStateMap[''] || null,
                };
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    getLoginReturnRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.authLink.hasParent()) {
                return this.getLoginReturnParentRequest();
            }
            return this.getLoginReturnHandle();
        });
    }
    getLoginReturnHandle() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = getWindowUrl();
            const authres = getUrlQueryParam(url, AuthQueryParam.response, this.authOptions.redirectUrlUseHash);
            if (authres) {
                let loginResponse = decodeQuery(authres);
                if (!loginResponse) {
                    loginResponse = yield this.authHttpClient.appGetAuthStorage(authres);
                }
                if (!loginResponse) {
                    throw new ExtError({ loginResponseMissing: true });
                }
                const replaceUrl = renderUrlQueryParams(url, { [AuthQueryParam.response]: undefined }, this.authOptions.redirectUrlUseHash);
                replaceWindowHistoryState(document.title, replaceUrl);
                yield this.applyLoginResponse(loginResponse);
            }
            return this.loginResponse;
        });
    }
    getLoginReturnParentRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGIN_RETURN);
            return response;
        });
    }
    getLoginReturnParentHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const loginResponse = yield this.getLoginReturnRequest();
            if (!loginResponse) {
                return null;
            }
            const responseStateMap = loginResponse.stateMap || {};
            return Object.assign(Object.assign({}, loginResponse), { stateMap: { ['']: responseStateMap[origin] || null } });
        });
    }
    /**
     * Refreshes session.
     * @returns Promise resolved with new session object.
     */
    refreshSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.authLink.hasParent()) {
                    return this.refreshSessionParentRequest();
                }
                const session = yield this.refreshSessionHandle();
                yield this.applySession(session);
                return this.authSession;
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    refreshSessionHandle() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.authHttpClient.refreshSession();
        });
    }
    refreshSessionParentRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_REFRESH_SESSION);
            return response;
        });
    }
    refreshSessionParentHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.refreshSession();
        });
    }
    logout(args) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const logoutRequest = {
                    env: this.authEnv,
                    options: this.authOptionsInput,
                    returnBy: makeDefined(args === null || args === void 0 ? void 0 : args.returnBy),
                    returnTo: makeDefined(args === null || args === void 0 ? void 0 : args.returnTo) || getWindowUrl(),
                    stateMap: { ['']: makeDefined(args === null || args === void 0 ? void 0 : args.state) },
                    reason: makeDefined(args === null || args === void 0 ? void 0 : args.reason),
                };
                yield this.applySession(null);
                yield this.applyLoginResponse(null);
                yield this.logoutRequest(logoutRequest);
                return this.getLogoutReturn();
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    /**
     * Initiates the redirect type logout float.
     * @param AuthLogoutRedirectArgs Logout redirect arguments object.
     * @returns Promise resolved with the logout flow is initiated.
     */
    logoutRedirect(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.logout({
                returnBy: AuthRequestReturnBy.redirect,
                returnTo: (args === null || args === void 0 ? void 0 : args.returnTo) || getWindowUrl(),
                state: args === null || args === void 0 ? void 0 : args.state,
                reason: args === null || args === void 0 ? void 0 : args.reason,
                logMessage: args === null || args === void 0 ? void 0 : args.logMessage,
            });
        });
    }
    logoutRequest(logoutRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.authLink.hasParent()) {
                return this.logoutParentRequest(logoutRequest);
            }
            return this.logoutHandle(logoutRequest);
        });
    }
    logoutHandle(logoutRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.beforeLogout);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGOUT);
            if (logoutRequest.returnBy === AuthRequestReturnBy.redirect) {
                this.applyLogoutResponse(null);
                yield this.authHttpClient.appLogoutRedirect(logoutRequest, this.authOptions.redirectUrlUseHash);
            }
            yield this.trigger(AuthEventType.afterLogout);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGOUT);
            return this.logoutResponse;
        });
    }
    logoutParentRequest(logoutRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_LOGOUT, logoutRequest);
            return response;
        });
    }
    logoutParentHandle(logoutRequest, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const beforeLogoutEvent = yield this.trigger(AuthEventType.beforeWindowLogout, null, {
                returnBy: logoutRequest.returnBy,
                returnTo: logoutRequest.returnTo ? getWindowUrl() : null,
                state: null,
                reason: logoutRequest.reason,
            });
            const { returnBy, returnTo, state, reason } = beforeLogoutEvent.result;
            const response = yield this.logoutRequest({
                env: this.authEnv,
                options: this.authOptionsInput,
                returnBy,
                returnTo,
                stateMap: {
                    ['']: state,
                    [origin]: logoutRequest.stateMap[''],
                },
                reason,
            });
            yield this.trigger(AuthEventType.afterWindowLogout);
            return response;
        });
    }
    /**
     * Requests and returns logout response data.
     * @returns Promise resolved with the logout response data.
     */
    getLogoutReturn() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const loginResponse = yield this.getLogoutReturnRequest();
                if (!loginResponse) {
                    return null;
                }
                const responseStateMap = loginResponse.stateMap || {};
                return {
                    success: loginResponse.success || null,
                    state: responseStateMap[''] || null,
                };
            }
            catch (error) {
                throw new ExtError({}, error);
            }
        });
    }
    getLogoutReturnRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.authLink.hasParent()) {
                return this.getLogoutReturnParentRequest();
            }
            return this.getLogoutReturnHandle();
        });
    }
    getLogoutReturnHandle() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = getWindowUrl();
            const authred = getUrlQueryParam(url, AuthQueryParam.redirection, this.authOptions.redirectUrlUseHash);
            if (authred) {
                const redirection = decodeQuery(authred);
                if (redirection.type === AuthRedirectionType.logout) {
                    let logoutRequest = redirection.logoutRequest;
                    if (!logoutRequest && redirection.authreqId) { // for backwards compatibility
                        logoutRequest = yield this.authHttpClient.appGetAuthStorage(redirection.authreqId);
                    }
                    if (!logoutRequest) {
                        throw new ExtError({ logoutRequestMissing: true });
                    }
                    const { stateMap } = logoutRequest;
                    const success = str2bool(getUrlQueryParam(url, AuthQueryParam.success, this.authOptions.redirectUrlUseHash)) === false ? false : true;
                    const logoutResponse = {
                        success,
                        stateMap,
                    };
                    const replaceUrl = renderUrlQueryParams(url, { [AuthQueryParam.redirection]: undefined, [AuthQueryParam.success]: undefined }, this.authOptions.redirectUrlUseHash);
                    replaceWindowHistoryState(document.title, replaceUrl);
                    yield this.applyLogoutResponse(logoutResponse);
                }
            }
            return this.logoutResponse;
        });
    }
    getLogoutReturnParentRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const { response } = yield this.linkParentRequest(AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGOUT_RETURN);
            return response;
        });
    }
    getLogoutReturnParentHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const loginResponse = yield this.getLogoutReturnRequest();
            if (!loginResponse) {
                return null;
            }
            const responseStateMap = loginResponse.stateMap || {};
            return Object.assign(Object.assign({}, loginResponse), { stateMap: { ['']: responseStateMap[origin] || null } });
        });
    }
    sessionChangeWindowHandle(authSession, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.applySession(authSession);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_SESSION_CHANGE, authSession);
        });
    }
    beforeParentLoginWindowHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.beforeParentLogin);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGIN);
        });
    }
    afterParentLoginWindowHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.afterParentLogin);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGIN);
        });
    }
    beforeParentLogoutWindowHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.beforeParentLogout);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGOUT);
        });
    }
    afterParentLogoutWindowHandle(origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.trigger(AuthEventType.afterParentLogout);
            yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGOUT);
        });
    }
    applySession(authSession) {
        return __awaiter(this, void 0, void 0, function* () {
            const authSessionValidated = validateAuthSession(authSession, this.authOptions);
            const authSessionNew = immutablyEvalAuthSession(authSessionValidated, this.authSession);
            if (this.authSession !== authSessionNew) {
                this.resetSessionExpiration(authSessionNew);
                this.resetSessionPolling();
                yield this.linkWindowsRequest(AUTH_LINK_WINDOW_REQUEST_EVENT_SESSION_CHANGE, authSessionNew);
            }
            if (authSessionNew) {
                if (!this.authSession) {
                    yield this.trigger(AuthEventType.sessionStart);
                }
                const { beforeExpiresIn } = evalAuthSessionExpiration(authSessionNew, this.authOptions);
                if (beforeExpiresIn <= 0) {
                    if (this.authOptions.sessionAutoRefresh) {
                        try {
                            yield this.refreshSession();
                        }
                        catch (error) {
                            yield this.trigger(AuthEventType.beforeSessionExpiration);
                        }
                    }
                    else {
                        yield this.trigger(AuthEventType.beforeSessionExpiration);
                    }
                }
            }
            else if (this.authSession) {
                const { expiresIn } = evalAuthSessionExpiration(this.authSession, this.authOptions);
                if (expiresIn <= 0) {
                    yield this.trigger(AuthEventType.sessionExpiration);
                }
                else {
                    yield this.trigger(AuthEventType.sessionEnd);
                }
            }
            return this.authSession = authSessionNew;
        });
    }
    resetSessionExpiration(authSession) {
        if (this.beforeExpirationTimer) {
            this.beforeExpirationTimer.destroy();
            this.beforeExpirationTimer = null;
        }
        if (this.expirationTimer) {
            this.expirationTimer.destroy();
            this.expirationTimer = null;
        }
        if (!authSession) {
            return;
        }
        const { beforeExpiresIn, expiresIn } = evalAuthSessionExpiration(authSession, this.authOptions);
        if (beforeExpiresIn > 0) {
            this.beforeExpirationTimer = createTimer(() => this.getSessionSilent(), beforeExpiresIn * 1000);
        }
        if (expiresIn > 0) {
            this.expirationTimer = createTimer(() => this.getSessionSilent(), expiresIn * 1000);
        }
    }
    resetSessionPolling() {
        if (this.pollingTimer) {
            this.pollingTimer.destroy();
            this.pollingTimer = null;
        }
        const sessionPollingIntervalTime = this.authOptions.sessionPollingIntervalTime;
        if (sessionPollingIntervalTime) {
            this.pollingTimer = createTimer(() => this.getSessionSilent(), sessionPollingIntervalTime * 1000, true);
        }
    }
    applyLoginResponse(loginResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            if (loginResponse && loginResponse.action) {
                yield this.applyLoginAction(loginResponse.action);
            }
            this.loginResponse = loginResponse;
        });
    }
    applyLoginAction(loginAction) {
        return __awaiter(this, void 0, void 0, function* () {
            if (loginAction.type === 'REDIRECT') {
                const { url } = loginAction;
                setWindowUrl(url);
            }
            else {
                throw new ExtError({ actionInvalid: true });
            }
        });
    }
    applyLogoutResponse(logoutResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            this.logoutResponse = logoutResponse;
        });
    }
    /**
     * Registers authentication events handler.
     * @param eventHandler Events handler function.
     * @returns Ubsubscribe function.
     */
    subscribe(eventHandler) {
        this.eventHandlers.push(eventHandler);
        const unsubscribe = () => {
            this.eventHandlers.splice(this.eventHandlers.indexOf(eventHandler), 1);
        };
        return unsubscribe;
    }
    trigger(type, data = null, resultValue = null) {
        return __awaiter(this, void 0, void 0, function* () {
            const event = new Event(type, data, resultValue);
            const eventRun = (eventHandlers, event) => __awaiter(this, void 0, void 0, function* () {
                const [eventHandler, ...eventHandlersRest] = eventHandlers;
                if (eventHandler) {
                    yield eventHandler(event);
                    return yield eventRun(eventHandlersRest, event);
                }
                return event;
            });
            return yield eventRun(this.eventHandlers, event);
        });
    }
    linkParentRequest(type, data = null, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = { type, data };
            const result = yield this.authLink.parentRequest(request, Object.assign(Object.assign({}, AUTH_LINK_PARENT_REQUEST_OPTIONS_DEFAULTS), options));
            const { response, origin } = result;
            return { response, origin };
        });
    }
    linkParentHandle(type, data, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_GET_SESSION) {
                return yield this.getSessionParentHandle(origin, source);
            }
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_LOGIN) {
                const loginRequest = data;
                return yield this.loginParentHandle(loginRequest, origin, source);
            }
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGIN_RETURN) {
                return yield this.getLoginReturnParentHandle(origin, source);
            }
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_REFRESH_SESSION) {
                return yield this.refreshSessionParentHandle(origin, source);
            }
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_LOGOUT) {
                const logoutRequest = data;
                return yield this.logoutParentHandle(logoutRequest, origin, source);
            }
            if (type === AUTH_LINK_PARENT_REQUEST_TYPE_GET_LOGOUT_RETURN) {
                return yield this.getLogoutReturnParentHandle(origin, source);
            }
        });
    }
    linkWindowsRequest(type, data = null, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = { type, data };
            const results = yield this.authLink.windowsRequest(request, Object.assign(Object.assign({}, AUTH_LINK_WINDOWS_REQUEST_OPTIONS_DEFAULTS), options));
            return results;
        });
    }
    linkWindowHandle(type, data, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (type === AUTH_LINK_WINDOW_REQUEST_EVENT_SESSION_CHANGE) {
                const authSession = data;
                return yield this.sessionChangeWindowHandle(authSession, origin, source);
            }
            if (type === AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGIN) {
                return yield this.beforeParentLoginWindowHandle(origin, source);
            }
            if (type === AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGIN) {
                return yield this.afterParentLoginWindowHandle(origin, source);
            }
            if (type === AUTH_LINK_WINDOW_REQUEST_EVENT_BEFORE_LOGOUT) {
                return yield this.beforeParentLogoutWindowHandle(origin, source);
            }
            if (type === AUTH_LINK_WINDOW_REQUEST_EVENT_AFTER_LOGOUT) {
                return yield this.afterParentLogoutWindowHandle(origin, source);
            }
        });
    }
}

const SECURITY_DOMAIN_CHECK_LEVEL_LIMIT = 2;
function securityDomainCheck(url1, url2) {
    const domain1 = urlGetDomain(url1, SECURITY_DOMAIN_CHECK_LEVEL_LIMIT);
    const domain2 = urlGetDomain(url2, SECURITY_DOMAIN_CHECK_LEVEL_LIMIT);
    return domain1 === domain2;
}

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_LINK_PARENT_REQUEST_OPTIONS_DEFAULTS, AUTH_LINK_WINDOWS_REQUEST_OPTIONS_DEFAULTS, AUTH_LOGOUT_REASON_LOG_MESSAGES, AUTH_OPTIONS_DEFAULTS, Auth, AuthEventType, AuthHttpClient, AuthLoginReason, AuthLogoutReason, AuthQueryParam, AuthRedirectionType, AuthRequestReturnBy, VALUE_STR2BOOL_FALSE_VALUES, VALUE_STR2BOOL_TRUE_VALUES, createTimer, decodeQuery, encodeQuery, evalAuthSessionExpiration, immutablyEvalAuthSession, isDefined, isSet, makeDefined, replaceWindowHistoryState, securityDomainCheck, sessionGet, sessionSet, str2bool, validateAuthSession, ɵ0 };

